import React from "react";
import { Chip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { SiteCommissioningItem } from "../../../../../dtos/siteCommissioning";
import { DynamicFieldsList, humanizeKey } from "./DynamicFieldsList";
import { PermissionNote } from "../console/PermissionNote";
import { tokens } from "../console/tokens";

const useStyles = makeStyles(() => ({
    note: {
        marginBottom: "12px",
    },
    card: {
        border: `1px solid ${tokens.line}`,
        borderRadius: "8px",
        padding: "12px",
        marginBottom: "12px",
        background: tokens.surface,
        "&:last-child": {
            marginBottom: 0,
        },
    },
    header: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        marginBottom: "8px",
    },
    serviceName: {
        fontSize: "13px",
        fontWeight: 600,
    },
}));

interface StatusStyle {
    label: string;
    background: string;
    color: string;
}

const SERVICE_STATUS_STYLES: Record<string, StatusStyle> = {
    started: { label: "Started", background: tokens.okBg, color: tokens.ok },
    stopped: { label: "Stopped", background: tokens.offBg, color: tokens.off },
};

function serviceIdFromKey(key: string): string {
    return key.startsWith("service:") ? key.slice("service:".length) : key;
}

function serviceStatusDisplay(status: unknown): StatusStyle | undefined {
    if (status == null || status === "") {
        return undefined;
    }
    if (typeof status === "string" && SERVICE_STATUS_STYLES[status]) {
        return SERVICE_STATUS_STYLES[status];
    }
    return { label: humanizeKey(String(status)), background: tokens.offBg, color: tokens.off };
}

interface Props {
    items: Record<string, SiteCommissioningItem>;
}

export const ServicesView: React.FC<Props> = ({ items }) => {
    const classes = useStyles();
    const entries = Object.entries(items);

    return (
        <>
            <div className={classes.note}>
                <PermissionNote>
                    Read-only. Services can only be started, stopped, or have auto-start changed from the commissioning site.
                </PermissionNote>
            </div>

            {entries.length === 0 ? (
                <Typography variant="body2">No services reported by the device yet.</Typography>
            ) : (
                entries.map(([key, item]) => {
                    const { serviceName, status, ...otherFields } = item.fields;
                    const statusDisplay = serviceStatusDisplay(status);

                    return (
                        <div key={key} className={classes.card}>
                            <div className={classes.header}>
                                <Typography component="span" className={classes.serviceName}>
                                    {typeof serviceName === "string" && serviceName ? serviceName : serviceIdFromKey(key)}
                                </Typography>
                                {statusDisplay && (
                                    <Chip
                                        size="small"
                                        label={statusDisplay.label}
                                        style={{ backgroundColor: statusDisplay.background, color: statusDisplay.color, fontWeight: 500 }}
                                    />
                                )}
                            </div>
                            <DynamicFieldsList fields={otherFields} />
                        </div>
                    );
                })
            )}
        </>
    );
};
